import { useEffect, useMemo, useState } from "react";
import { Info, Search, Trash2, ThumbsUp, Copy } from "lucide-react";
import { toast } from "sonner";
import { PortalPage, Spinner, ErrorNote } from "@/components/PortalPage";
import { api } from "@/lib/api";

const PATTERN = { line: "Line", rectangle: "Rectangle", round: "Round", sine_line: "Sine Line", spiral: "Spiral", lissajous: "Lissajous", flower: "Flower", honeycomb: "Honeycomb" };
const MODELS = ["300W", "500W", "1000W", "2000W"];
const SUB_LABEL = { metal: "Metal", other: "Other", wood: "Wood" };
const label = (s) => SUB_LABEL[s] || s;

const Tile = ({ k, v }) => (
  <div style={{ flex: "1 1 0", minWidth: 92, padding: "8px 10px", borderRadius: 8, background: "#0A0A0A", border: "1px solid #333333" }}>
    <p style={{ color: "#A0A0A0", fontSize: 10, fontWeight: 700, letterSpacing: 0.8 }}>{k}</p>
    <p style={{ color: "#FFFFFF", fontSize: 15, fontWeight: 700 }}>{v}</p>
  </div>
);

export const RecipeCard = ({ p, actions }) => {
  const tiles = [
    ["Power", `${p.power_percent}%`], ["Freq", `${p.frequency_khz} kHz`], ["Pulse", `${p.pulse_width_ns} ns`],
    ["Speed", `${Number(p.speed_mm_s).toLocaleString()} mm/s`], ["Pattern size", `${p.pattern_size_mm} mm`],
    ["Hatch", p.hatch_2_mm != null ? `${p.hatch_1_mm} × ${p.hatch_2_mm} mm` : `${p.hatch_1_mm} mm`],
    ...(p.metal_thickness_mm != null ? [["Thickness", `${p.metal_thickness_mm} mm`]] : []),
  ];
  return (
    <div data-testid={`recipe-${p.id}`} style={{ background: "#1A1A1A", border: "1px solid #333333", borderRadius: 14, padding: 16 }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 10 }}>
        <span style={{ padding: "3px 8px", borderRadius: 6, border: "1px solid #4CCC06", background: "rgba(76, 204, 6,0.08)", color: "#4CCC06", fontSize: 12, fontWeight: 800 }}>{p.laser_model}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ color: "#FFFFFF", fontSize: 15, fontWeight: 700 }}>{p.substrate_detail}</p>
          <p style={{ color: "#A0A0A0", fontSize: 12 }}>{[p.contaminant, PATTERN[p.pattern] || p.pattern].filter(Boolean).join(" • ")}</p>
        </div>
        {actions}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>{tiles.map(([k, v]) => <Tile key={k} k={k} v={v} />)}</div>
      {p.notes && (
        <div style={{ borderTop: "1px solid #2a2a2a", marginTop: 12, paddingTop: 10 }}>
          <p style={{ color: "#4CCC06", fontSize: 10, fontWeight: 800, letterSpacing: 1.2 }}>NOTES</p>
          <p style={{ color: "#FFFFFF", fontSize: 13, lineHeight: "19px", marginTop: 4 }}>{p.notes}</p>
        </div>
      )}
    </div>
  );
};

const Chip = ({ on, children, ...rest }) => (
  <button {...rest} style={{ padding: "7px 16px", borderRadius: 999, fontSize: 12, fontWeight: 700, background: on ? "#4CCC06" : "#242424", color: on ? "#000" : "#FFFFFF", border: `1px solid ${on ? "#4CCC06" : "#333"}` }}>{children}</button>
);

export const useFilters = (list) => {
  const [q, setQ] = useState("");
  const [model, setModel] = useState(null);
  const [sub, setSub] = useState(null);
  const subs = useMemo(() => [...new Set((list || []).map((p) => p.substrate_category))].sort(), [list]);
  const shown = useMemo(() => (list || []).filter((p) => (!model || p.laser_model === model) && (!sub || p.substrate_category === sub)
    && (!q.trim() || `${p.substrate_detail} ${p.contaminant || ""} ${p.pattern} ${p.notes || ""}`.toLowerCase().includes(q.trim().toLowerCase()))), [list, q, model, sub]);
  const ui = (
    <>
      <label style={{ display: "flex", alignItems: "center", gap: 10, background: "#1A1A1A", border: "1px solid #333", borderRadius: 10, padding: "0 16px" }}>
        <Search size={16} color="#A0A0A0" />
        <input data-testid="recipe-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search substrate, contaminant, pattern…" style={{ flex: 1, background: "transparent", color: "#fff", outline: "none", padding: "11px 0", fontSize: 14 }} />
      </label>
      <p style={{ color: "#A0A0A0", fontSize: 11, fontWeight: 700, letterSpacing: 1.2, marginTop: 4 }}>LASER MODEL</p>
      <div className="flex flex-wrap gap-2">
        <Chip on={!model} onClick={() => setModel(null)} data-testid="recipe-model-all">All Models</Chip>
        {MODELS.map((m) => <Chip key={m} on={model === m} onClick={() => setModel(m)} data-testid={`recipe-model-${m}`}>{m}</Chip>)}
      </div>
      <p style={{ color: "#A0A0A0", fontSize: 11, fontWeight: 700, letterSpacing: 1.2 }}>SUBSTRATE</p>
      <div className="flex flex-wrap gap-2">
        <Chip on={!sub} onClick={() => setSub(null)} data-testid="recipe-sub-all">All Substrates</Chip>
        {subs.map((s) => <Chip key={s} on={sub === s} onClick={() => setSub(s)} data-testid={`recipe-sub-${s}`}>{label(s)}</Chip>)}
      </div>
    </>
  );
  return [shown, ui];
};

const Count = ({ n }) => <span data-testid="recipe-count" style={{ minWidth: 34, padding: "4px 10px", borderRadius: 999, border: "1px solid #4CCC06", color: "#4CCC06", fontSize: 12, fontWeight: 800, textAlign: "center" }}>{n}</span>;

export function CleaningSettingsLibrary() {
  const [list, setList] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => { api("/ai/training/settings/public").then(setList).catch((e) => setErr(e.message)); }, []);
  const [shown, filters] = useFilters(list);
  return (
    <PortalPage eyebrow="LASER CLEANING SETTINGS" title="Browse Recipe Library" testId="cleaning-settings-page" width="max-w-6xl" actions={list && <Count n={shown.length} />}>
      <div data-testid="recipe-info-banner" style={{ display: "flex", gap: 10, alignItems: "center", padding: "10px 14px", borderRadius: 8, border: "1px solid rgba(76, 204, 6,0.35)", background: "rgba(76, 204, 6,0.06)", color: "#fff", fontSize: 12 }}>
        <Info size={14} color="#4CCC06" className="shrink-0" />Curated technical recipes from ALR's franchisee network. Read-only — always run a small test patch on your own substrate before committing to a job.
      </div>
      {filters}
      <ErrorNote>{err}</ErrorNote>
      {!list && !err && <Spinner />}
      {shown.map((p) => <RecipeCard key={p.id} p={p} />)}
      {list && shown.length === 0 && <p className="text-sm text-neutral-400" data-testid="recipe-empty">No recipes match.</p>}
    </PortalPage>
  );
}

export function MyPresets() {
  const [tab, setTab] = useState("me");
  const [list, setList] = useState(null);
  const [err, setErr] = useState(null);
  const load = () => { setList(null); api(`/ai/training/settings/${tab === "me" ? "me" : "community"}`).then(setList).catch((e) => setErr(e.message)); };
  useEffect(load, [tab]); // eslint-disable-line react-hooks/exhaustive-deps
  const [shown, filters] = useFilters(list);
  const act = async (fn, ok) => { try { await fn(); toast.success(ok); load(); } catch (e) { toast.error(e.message); } };
  const iconBtn = { padding: 6, borderRadius: 8, border: "1px solid #333", color: "#A0A0A0" };
  return (
    <PortalPage eyebrow="LASER CLEANING SETTINGS" title="My Cleaning Settings" testId="my-presets-page" width="max-w-6xl" actions={list && <Count n={shown.length} />}>
      <div className="flex gap-2">
        <Chip on={tab === "me"} onClick={() => setTab("me")} data-testid="presets-tab-me">My Settings</Chip>
        <Chip on={tab === "community"} onClick={() => setTab("community")} data-testid="presets-tab-community">Community</Chip>
        <a href="/laser-machines" data-testid="presets-machines-link" style={{ padding: "7px 16px", borderRadius: 999, fontSize: 12, fontWeight: 700, border: "1px solid #4CCC06", color: "#4CCC06" }}>Machines & Calibration</a>
        <a href="/cleaning-settings" data-testid="presets-browse-library" style={{ marginLeft: "auto", color: "#4CCC06", fontSize: 12, fontWeight: 700, alignSelf: "center" }}>Browse Recipe Library →</a>
      </div>
      {filters}
      <ErrorNote>{err}</ErrorNote>
      {!list && !err && <Spinner />}
      {list && shown.length === 0 && <p className="text-sm text-neutral-400" data-testid="presets-empty">{tab === "me" ? "No saved settings yet — tap SAVE on any settings card in the Cleaning Agent, or use Add Setting." : "No community settings yet."}</p>}
      {shown.map((p) => (
        <RecipeCard key={p.id} p={p} actions={tab === "me"
          ? <button style={iconBtn} aria-label="Delete" data-testid={`preset-delete-${p.id}`} onClick={() => window.confirm("Delete this setting?") && act(() => api(`/ai/training/settings/me/${p.id}`, { method: "DELETE" }), "Deleted")}><Trash2 size={16} /></button>
          : <span className="flex gap-1">
              <button style={iconBtn} aria-label="Upvote" data-testid={`preset-upvote-${p.id}`} onClick={() => act(() => api(`/ai/training/settings/${p.id}/upvote`, { method: "POST" }), "Thanks!")}><ThumbsUp size={16} /></button>
              <button style={iconBtn} aria-label="Copy to mine" data-testid={`preset-copy-${p.id}`} onClick={() => act(() => api(`/ai/training/settings/${p.id}/copy`, { method: "POST" }), "Copied to My Settings")}><Copy size={16} /></button>
            </span>} />
      ))}
    </PortalPage>
  );
}
